/**
 * React-Query bindings for the three LiveKit callables:
 *   • `testLiveKitConnection` — credential probe + SIP-URI derivation
 *   • `mintLiveKitToken`      — generic room-join token (rarely used directly)
 *   • `startLiveKitTestSession` — orchestrates browser test-chat
 *
 * All three are admin-target-aware via `useTargetUidPayload`: when an
 * admin views `/admin/users/:uid/settings`, the callables operate on
 * the target user's LK credentials (not the admin's own).
 */
import { useMutation } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useTargetUidPayload } from './useTargetUidPayload';

export interface LiveKitProbeInput {
  url?: string;
  apiKey?: string;
  apiSecret?: string;
}

export interface LiveKitProbeResult {
  ok: true;
  sipUri: string;
}

export function useTestLiveKitConnection() {
  const adminPayload = useTargetUidPayload();
  return useMutation({
    mutationFn: async (input: LiveKitProbeInput) => {
      const fn = httpsCallable<LiveKitProbeInput & { uid?: string }, LiveKitProbeResult>(
        functions,
        'testLiveKitConnection',
      );
      const r = await fn({ ...input, ...adminPayload });
      return r.data;
    },
  });
}

export interface LiveKitTokenResult {
  token: string;
  url: string;
}

export function useMintLiveKitToken() {
  const adminPayload = useTargetUidPayload();
  return useMutation({
    mutationFn: async (input: { room: string; identity?: string; metadata?: string }) => {
      const fn = httpsCallable<typeof input & { uid?: string }, LiveKitTokenResult>(
        functions,
        'mintLiveKitToken',
      );
      const r = await fn({ ...input, ...adminPayload });
      return r.data;
    },
  });
}

export interface LiveKitTestSessionResult extends LiveKitTokenResult {
  room: string;
}

/**
 * Spins up a fresh test session. The Cloud Function:
 *   1. mints two tokens (browser + agent)
 *   2. POSTs to Cloud Run `/livekit/start` so the Pipecat pipeline
 *      joins the same room as `cp3-agent`
 *   3. returns the browser token + url + room name
 *
 * The caller then uses `useLiveKitSession.connect()` with the returned
 * token/url to actually join.
 */
export function useStartLiveKitTestSession() {
  const adminPayload = useTargetUidPayload();
  return useMutation({
    mutationFn: async () => {
      const fn = httpsCallable<{ uid?: string }, LiveKitTestSessionResult>(
        functions,
        'startLiveKitTestSession',
      );
      const r = await fn(adminPayload);
      return r.data;
    },
  });
}
